import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Bot, Plus, Trash2, RefreshCw, Zap, CheckCircle,
    ExternalLink, BarChart2,
    Calendar, Lightbulb, Building2, Globe, FileText, Loader,
    Play, AlertTriangle, Info, Shield, Rocket, Target, TrendingUp, ClipboardList,
    Activity, Search, Copy, Clock, ToggleLeft, ToggleRight, ShieldCheck,
    MessageSquare, Star,
} from 'lucide-react';
import {
    GlassCard, GlassButton, GlassInput, GlassTabs, GlassBadge,
    GlassModal, ModuleHeader, GlassSelect,
} from '../ui';
import {
    apiClient,
    getCompetitors, addCompetitor, deleteCompetitor, analyzeCompetitor,
    getLatestCompetitorReport,
    mineCompetitorReviews, getLatestReviewInsight, promoteReviewTopicsToBriefs,
    generateContentCalendar, getContentCalendars, updateContentCalendar,
    generateMonthlyReport, getMonthlyReports, getMonthlyReport,
    getContentSuggestions,
    getAutonomySettings,
    executeCalendarPlan,
    autonomousGenerateContent,
    getAutonomousContentStatus,
    identifyCampaignOpportunities,
    proposeCampaignBrief,
    autoLaunchCampaign,
    getCampaignAdjustmentSuggestions,
    generateCampaignRetrospective,
    scoreLeads,
    autoEnrollLeads,
    getNurtureHealth,
    reEngageLeads,
    getPlatformHealth,
    detectAnomalies,
    generateExecutiveReport,
    scheduleReport,
    getReportSchedule,
    getRecentReports,
} from '../../services/api';
import './Agent.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Types ───────────────────────────────────────────────────────────────────

interface OutletCtx {
    selectedProjectId?: string;
}

interface Competitor {
    competitor_id: string;
    name: string;
    website_url?: string;
    notes?: string;
    last_analyzed_at?: string;
}

/** Minimal shared shape for ConfidenceBadge — both IntelReport and ReviewInsight satisfy it. */
interface ConfidenceFields {
    confidence_score?: number | null;
    confidence_note?: string | null;
    data_sources?: string[] | null;
}

interface IntelReport extends ConfidenceFields {
    report_id: string;
    summary: string;
    content_topics: string[];
    keyword_gaps: string[];
    content_gaps: string[];
    strengths: string[];
    opportunities: string[];
    report_date: string;
}

interface ReviewInsight extends ConfidenceFields {
    insight_id: string;
    competitor_id: string;
    created_at: string;
    reviews_analyzed: number;
    reviews_available?: number | null;
    source_query?: string | null;
    provider?: string | null;
    avg_rating?: number | null;
    common_questions: { question: string; evidence_count: number }[];
    pain_points: { pain_point: string; evidence_count: number }[];
    objections: { objection: string; evidence_count: number }[];
    derived_topics: { title: string; target_keyword: string; angle: string; source: string }[];
}

interface CalendarItem {
    week: number;
    content_type: string;
    topic: string;
    target_keyword: string;
    platform: string;
    rationale: string;
    brief?: string;
    /** SERP-grounding signals — additive; absent on legacy plans */
    difficulty?: 'low' | 'medium' | 'high' | string | null;
    competitors?: string[] | null;        // competing page URLs
    pillar?: string | null;
    grounded?: boolean | null;
}

interface CalendarPlan {
    plan_id: string;
    month: string;
    status: string;
    plan_data: CalendarItem[];
    ai_rationale: string;
    created_at: string;
}

interface ReportSummary {
    report_id: string;
    month: string;
    headline: string;
    generated_at: string;
}

interface FullReport extends ReportSummary {
    content: {
        sections: { title: string; content?: string; items?: string[] }[];
        kpis: Record<string, number>;
    };
    html_content: string;
}

interface Suggestion {
    topic: string;
    content_type: string;
    urgency: 'high' | 'medium' | 'low';
    reason: string;
    /** SERP-grounding signals — additive; absent on legacy suggestions */
    target_keyword?: string | null;
    difficulty?: 'low' | 'medium' | 'high' | string | null;
    competitors?: string[] | null;        // competing page URLs
    pillar?: string | null;
    grounded?: boolean | null;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso?: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric',
        });
    } catch {
        return iso;
    }
}

function urgencyVariant(u: string): 'danger' | 'warning' | 'default' {
    if (u === 'high') return 'danger';
    if (u === 'medium') return 'warning';
    return 'default';
}

function contentTypeBadge(ct: string): string {
    return ct.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Grounding / Confidence helpers ──────────────────────────────────────────

/** Confidence badge for a competitor intel report or review insight. Legacy rows (null score) render no badge. */
const ConfidenceBadge: React.FC<{ report: ConfidenceFields; kind?: 'intel' | 'review' }> = ({ report, kind = 'intel' }) => {
    if (report.confidence_score == null) return null; // legacy row — render exactly as before
    const sources = report.data_sources || [];
    const hasSite = sources.includes('site_fetch');
    const hasSerp = sources.includes('serp');

    let label: string;
    let tone: 'success' | 'warning';
    let Icon: typeof ShieldCheck;

    const hasReviews = sources.includes('google_reviews');

    if (hasSite && hasSerp) {
        label = 'Grounded: website + search'; tone = 'success'; Icon = ShieldCheck;
    } else if (hasSite) {
        label = 'Grounded: website'; tone = 'success'; Icon = Globe;
    } else if (hasSerp) {
        label = 'Partially grounded'; tone = 'warning'; Icon = Globe;
    } else if (hasReviews) {
        // google_reviews source: tone by confidence band
        if (report.confidence_score >= 0.8) {
            label = 'Grounded: customer reviews'; tone = 'success'; Icon = ShieldCheck;
        } else if (report.confidence_score >= 0.5) {
            label = 'Limited reviews sampled'; tone = 'warning'; Icon = Globe;
        } else {
            label = 'Very few reviews'; tone = 'warning'; Icon = AlertTriangle;
        }
    } else if (report.confidence_score <= 0.3) {
        // review context: degraded mine found no reviews; intel context: AI-knowledge-only analysis
        label = kind === 'review' ? 'No reviews found' : 'Unverified: AI knowledge only';
        tone = 'warning'; Icon = AlertTriangle;
    } else {
        return null;
    }

    return (
        <span className={`confidence-badge confidence-badge--${tone}`}>
            <Icon size={16} />
            {label}
        </span>
    );
};

function difficultyTone(d: string): 'success' | 'warning' | 'danger' | 'default' {
    const v = d.toLowerCase();
    if (v === 'low') return 'success';
    if (v === 'medium') return 'warning';
    if (v === 'high') return 'danger';
    return 'default';
}

interface GroundingFields {
    target_keyword?: string | null;
    difficulty?: string | null;
    competitors?: string[] | null;
    pillar?: string | null;
    grounded?: boolean | null;
}

/**
 * Per-item grounding meta row for calendar items & content suggestions.
 * All fields are optional — legacy payloads with none of them render nothing.
 */
const GroundingMeta: React.FC<{ item: GroundingFields; showKeyword?: boolean }> = ({ item, showKeyword = false }) => {
    const competitorCount = item.competitors?.length || 0;
    const hasAny = Boolean(
        item.difficulty
        || item.grounded === true
        || (item.grounded === false && item.target_keyword)
        || competitorCount > 0
        || item.pillar
        || (showKeyword && item.target_keyword)
    );
    if (!hasAny) return null;

    return (
        <div className="grounding-meta">
            {showKeyword && item.target_keyword && (
                <span className="grounding-keyword" title="Target keyword">kw: {item.target_keyword}</span>
            )}
            {item.difficulty && (
                <span
                    className={`difficulty-chip difficulty-chip--${difficultyTone(item.difficulty)}`}
                    title="Keyword difficulty"
                >
                    {item.difficulty} difficulty
                </span>
            )}
            {item.grounded === true && (
                <span className="grounding-badge grounding-badge--serp" title="Backed by live search results">
                    <Search size={12} />
                    SERP-grounded
                </span>
            )}
            {item.grounded === false && item.target_keyword && (
                <span className="grounding-badge grounding-badge--ai" title="Suggested from AI knowledge, not live search data">
                    AI-suggested
                </span>
            )}
            {competitorCount > 0 && (
                <span className="grounding-competitors" title={(item.competitors || []).join('\n')}>
                    <Globe size={12} />
                    {competitorCount} competing page{competitorCount !== 1 ? 's' : ''}
                </span>
            )}
            {item.pillar && (
                <span className="pillar-tag" title="Content pillar">{item.pillar}</span>
            )}
        </div>
    );
};

const TABS = [
    { id: 'competitors', label: 'Competitor Intelligence' },
    { id: 'calendar', label: 'Content Calendar' },
    { id: 'report', label: 'Monthly Report' },
    { id: 'suggestions', label: 'Content Suggestions' },
    { id: 'autonomous', label: 'Autonomous Execution' },
    { id: 'autonomous_campaigns', label: 'Auto Campaigns' },
    { id: 'autonomous_leads', label: 'Auto Leads' },
    { id: 'autonomous_monitoring', label: 'Auto Monitor' },
];

const CONTENT_TYPE_OPTIONS = [
    { value: 'blog_post', label: 'Blog Post' },
    { value: 'social_linkedin', label: 'LinkedIn Post' },
    { value: 'social_twitter', label: 'Twitter Thread' },
    { value: 'email', label: 'Email' },
    { value: 'landing_page', label: 'Landing Page Copy' },
];

// ── Main Component ───────────────────────────────────────────────────────────

const AgentModule: React.FC = () => {
    const ctx = useOutletContext<OutletCtx>();
    const selectedProjectId = ctx?.selectedProjectId;

    const [activeTab, setActiveTab] = useState('competitors');
    const [projectId, setProjectId] = useState<string>(selectedProjectId || '');

    // Update projectId when outlet context changes
    useEffect(() => {
        if (selectedProjectId) setProjectId(selectedProjectId);
    }, [selectedProjectId]);

    // ── Competitor State ────────────────────────────────────────────────────
    const [competitors, setCompetitors] = useState<Competitor[]>([]);
    const [competitorsLoading, setCompetitorsLoading] = useState(false);
    const [addCompetitorOpen, setAddCompetitorOpen] = useState(false);
    const [newCompetitorName, setNewCompetitorName] = useState('');
    const [newCompetitorUrl, setNewCompetitorUrl] = useState('');
    const [newCompetitorNotes, setNewCompetitorNotes] = useState('');
    const [addingCompetitor, setAddingCompetitor] = useState(false);
    const [analyzingId, setAnalyzingId] = useState<string | null>(null);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [activeReport, setActiveReport] = useState<IntelReport | null>(null);
    const [reportLoading, setReportLoading] = useState(false);

    // ── Review Mining State ─────────────────────────────────────────────────
    const [miningId, setMiningId] = useState<string | null>(null);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [activeInsight, setActiveInsight] = useState<ReviewInsight | null>(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [promotingTopics, setPromotingTopics] = useState(false);

    // ── Calendar State ──────────────────────────────────────────────────────
    const [calendarMonth, setCalendarMonth] = useState<string>(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [calendarGoals, setCalendarGoals] = useState('');
    const [calendarTopics, setCalendarTopics] = useState('');
    const [calendarGenerating, setCalendarGenerating] = useState(false);
    const [currentPlan, setCurrentPlan] = useState<CalendarPlan | null>(null);
    const [pastPlans, setPastPlans] = useState<CalendarPlan[]>([]);
    const [executingItem, setExecutingItem] = useState<number | null>(null);
    const [executedItems, setExecutedItems] = useState<Set<number>>(new Set());

    // ── Monthly Report State ────────────────────────────────────────────────
    const [reportMonth, setReportMonth] = useState<string>(() => {
        const now = new Date();
        // Default to previous month
        const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });
    const [reportGenerating, setReportGenerating] = useState(false);
    const [currentReport, setCurrentReport] = useState<FullReport | null>(null);
    const [pastReports, setPastReports] = useState<ReportSummary[]>([]);

    // ── Suggestions State ───────────────────────────────────────────────────
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);

    // ── Autonomous Execution State ──────────────────────────────────────────
    const [autonomySettings, setAutonomySettings] = useState<any>(null);
    const [autonomyStatus, setAutonomyStatus] = useState<any>(null);
    const [autonomyStatusLoading, setAutonomyStatusLoading] = useState(false);

    // Execute calendar plan
    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [selectedPlanItems, setSelectedPlanItems] = useState<Set<number>>(new Set());
    const [planToExecute, setPlanToExecute] = useState<CalendarPlan | null>(null);
    const [executing, setExecuting] = useState(false);
    const [execResult, setExecResult] = useState<any>(null);

    // Quick generate
    const [genContentType, setGenContentType] = useState('blog_post');
    const [genTopic, setGenTopic] = useState('');
    const [genKeyword, setGenKeyword] = useState('');
    const [genPlatform, setGenPlatform] = useState('');
    const [genBrief, setGenBrief] = useState('');
    const [generating, setGenerating] = useState(false);
    const [genResult, setGenResult] = useState<any>(null);

    // ── Autonomous Campaigns State ──────────────────────────────────────────
    const [oppLoading, setOppLoading] = useState(false);
    const [opportunities, setOpportunities] = useState<any[]>([]);
    const [oppError, setOppError] = useState('');

    const [briefLoading, setBriefLoading] = useState(false);
    const [proposedBrief, setProposedBrief] = useState<any>(null);
    const [briefOutcome, setBriefOutcome] = useState<any>(null);

    const [launchLoading, setLaunchLoading] = useState(false);
    const [launchResult, setLaunchResult] = useState<any>(null);

    // Campaign advisor
    const [campaigns, setCampaigns] = useState<any[]>([]);
    const [campaignsLoading, setCampaignsLoading] = useState(false);
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [adjLoading, setAdjLoading] = useState(false);
    const [adjSuggestions, setAdjSuggestions] = useState<any[]>([]);
    const [adjError, setAdjError] = useState('');
    const [retroLoading, setRetroLoading] = useState(false);
    const [retroResult, setRetroResult] = useState<any>(null);
    const [retroExpanded, setRetroExpanded] = useState(false);

    // ── Autonomous Leads State ──────────────────────────────────────────────
    const [nurtureHealth, setNurtureHealth] = useState<any>(null);
    const [nurtureHealthLoading, setNurtureHealthLoading] = useState(false);

    const [scoredLeads, setScoredLeads] = useState<any[]>([]);
    const [scoringLoading, setScoringLoading] = useState(false);
    const [scoringError, setScoringError] = useState('');

    const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
    const [sequences, setSequences] = useState<any[]>([]);
    const [selectedSequenceId, setSelectedSequenceId] = useState('');
    const [enrollLoading, setEnrollLoading] = useState(false);
    const [enrollResult, setEnrollResult] = useState<any>(null);

    const [reEngageDays, setReEngageDays] = useState('30');
    const [reEngageLoading, setReEngageLoading] = useState(false);
    const [reEngageResult, setReEngageResult] = useState<any>(null);

    // ── Autonomous Monitoring State ─────────────────────────────────────────
    const [platformHealth, setPlatformHealth] = useState<any>(null);
    const [healthLoading, setHealthLoading] = useState(false);

    const [anomalies, setAnomalies] = useState<any[]>([]);
    const [anomalyLoading, setAnomalyLoading] = useState(false);
    const [anomalyScanned, setAnomalyScanned] = useState(false);

    const [execReportPeriod, setExecReportPeriod] = useState('weekly');
    const [execReportLoading, setExecReportLoading] = useState(false);
    const [execReport, setExecReport] = useState<any>(null);
    const [recentReports, setRecentReports] = useState<any[]>([]);

    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [scheduleSaving, setScheduleSaving] = useState(false);
    const [scheduleFrequency, setScheduleFrequency] = useState('weekly');
    const [scheduleDow, setScheduleDow] = useState('1');
    const [scheduleHour, setScheduleHour] = useState('9');
    const [scheduleEnabled, setScheduleEnabled] = useState(false);

    // ── Load Competitors ────────────────────────────────────────────────────
    const loadCompetitors = useCallback(async () => {
        if (!projectId) return;
        setCompetitorsLoading(true);
        try {
            const data = await getCompetitors(projectId);
            setCompetitors(data);
        } catch (err) {
            console.error('Failed to load competitors', err);
        } finally {
            setCompetitorsLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'competitors' && projectId) {
            loadCompetitors();
        }
    }, [activeTab, projectId, loadCompetitors]);

    // ── Load Past Plans ─────────────────────────────────────────────────────
    const loadPastPlans = useCallback(async () => {
        if (!projectId) return;
        try {
            const data = await getContentCalendars(projectId);
            setPastPlans(data);
        } catch (err) {
            console.error('Failed to load past plans', err);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'calendar' && projectId) {
            loadPastPlans();
        }
    }, [activeTab, projectId, loadPastPlans]);

    // ── Load Past Reports ───────────────────────────────────────────────────
    const loadPastReports = useCallback(async () => {
        if (!projectId) return;
        try {
            const data = await getMonthlyReports(projectId);
            setPastReports(data);
        } catch (err) {
            console.error('Failed to load past reports', err);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'report' && projectId) {
            loadPastReports();
        }
    }, [activeTab, projectId, loadPastReports]);

    // ── Load Suggestions ────────────────────────────────────────────────────
    const loadSuggestions = useCallback(async () => {
        if (!projectId) return;
        setSuggestionsLoading(true);
        try {
            const data = await getContentSuggestions(projectId);
            setSuggestions(data.suggestions || []);
        } catch (err) {
            console.error('Failed to load suggestions', err);
        } finally {
            setSuggestionsLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'suggestions' && projectId) {
            loadSuggestions();
        }
    }, [activeTab, projectId, loadSuggestions]);

    // ── Load Autonomy Data ───────────────────────────────────────────────────
    const loadAutonomyData = useCallback(async () => {
        setAutonomyStatusLoading(true);
        try {
            const [settings, status] = await Promise.all([
                getAutonomySettings(),
                getAutonomousContentStatus(projectId || undefined),
            ]);
            setAutonomySettings(settings);
            setAutonomyStatus(status);
        } catch (err) {
            console.error('Failed to load autonomy data', err);
        } finally {
            setAutonomyStatusLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'autonomous') {
            loadAutonomyData();
            if (projectId) loadPastPlans();
        }
    }, [activeTab, projectId, loadAutonomyData, loadPastPlans]);

    // ── Load Campaigns for Advisor ───────────────────────────────────────────
    const loadCampaigns = useCallback(async () => {
        setCampaignsLoading(true);
        try {
            const resp = await apiClient.get('/campaigns/', {
                params: projectId ? { project_id: projectId } : {},
            });
            setCampaigns(resp.data || []);
        } catch (err) {
            console.error('Failed to load campaigns', err);
        } finally {
            setCampaignsLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'autonomous_campaigns') {
            loadCampaigns();
        }
    }, [activeTab, projectId, loadCampaigns]);

    // When selected plan changes, reset item selection and load its items
    useEffect(() => {
        if (!selectedPlanId) {
            setPlanToExecute(null);
            setSelectedPlanItems(new Set());
            return;
        }
        const found = pastPlans.find(p => p.plan_id === selectedPlanId);
        if (found) {
            setPlanToExecute(found);
            // Select all items by default
            const allIndices = new Set((found.plan_data || []).map((_: any, i: number) => i));
            setSelectedPlanItems(allIndices);
        }
    }, [selectedPlanId, pastPlans]);

    const toggleItemSelection = (idx: number) => {
        setSelectedPlanItems(prev => {
            const next = new Set(Array.from(prev));
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    };

    const handleExecuteCalendar = async () => {
        if (!planToExecute || !projectId) return;
        setExecuting(true);
        setExecResult(null);
        try {
            const result = await executeCalendarPlan(planToExecute.plan_id, {
                project_id: projectId,
                execute_all: false,
                item_indices: Array.from(selectedPlanItems),
            });
            setExecResult(result);
            loadAutonomyData();
        } catch (err: any) {
            console.error('Execute calendar plan failed', err);
            alert(getApiError(err, 'Execution failed. Please try again.'));
        } finally {
            setExecuting(false);
        }
    };

    const handleQuickGenerate = async () => {
        if (!projectId || !genTopic.trim()) return;
        setGenerating(true);
        setGenResult(null);
        try {
            const result = await autonomousGenerateContent({
                project_id: projectId,
                content_type: genContentType,
                topic: genTopic.trim(),
                target_keyword: genKeyword.trim() || undefined,
                platform: genPlatform.trim() || undefined,
                brief: genBrief.trim() || undefined,
            });
            setGenResult(result);
            loadAutonomyData();
        } catch (err: any) {
            console.error('Quick generate failed', err);
            alert(getApiError(err, 'Generation failed. Please try again.'));
        } finally {
            setGenerating(false);
        }
    };

    // ── Autonomous Campaign Handlers ────────────────────────────────────────

    const handleIdentifyOpportunities = async () => {
        setOppLoading(true);
        setOppError('');
        setOpportunities([]);
        setProposedBrief(null);
        setBriefOutcome(null);
        setLaunchResult(null);
        try {
            const res = await identifyCampaignOpportunities();
            setOpportunities(res.opportunities || []);
            if ((res.opportunities || []).length === 0) {
                setOppError('No opportunities identified. Try adding more campaigns or brand guides.');
            }
        } catch (err: any) {
            setOppError(getApiError(err, 'Failed to identify opportunities. Please try again.'));
        } finally {
            setOppLoading(false);
        }
    };

    const handleProposeBrief = async (opportunity: any) => {
        setBriefLoading(true);
        setBriefOutcome(null);
        setProposedBrief(null);
        setLaunchResult(null);
        try {
            const res = await proposeCampaignBrief(opportunity);
            setProposedBrief(res.brief || null);
            setBriefOutcome({ action: res.action, item_id: res.item_id });
        } catch (err: any) {
            alert(getApiError(err, 'Failed to propose brief. Please try again.'));
        } finally {
            setBriefLoading(false);
        }
    };

    const handleAutoLaunch = async () => {
        if (!proposedBrief) return;
        setLaunchLoading(true);
        setLaunchResult(null);
        try {
            const res = await autoLaunchCampaign(
                proposedBrief,
                proposedBrief.suggested_assets,
                undefined,
                projectId || undefined,
            );
            setLaunchResult(res);
            if (res.action === 'executed' && res.campaign?.campaign_id) {
                loadCampaigns();
            }
        } catch (err: any) {
            alert(getApiError(err, 'Auto-launch failed. Please try again.'));
        } finally {
            setLaunchLoading(false);
        }
    };

    const handleGetAdjustments = async () => {
        if (!selectedCampaignId) return;
        setAdjLoading(true);
        setAdjError('');
        setAdjSuggestions([]);
        setRetroResult(null);
        try {
            const res = await getCampaignAdjustmentSuggestions(selectedCampaignId);
            setAdjSuggestions(res.suggestions || []);
            if ((res.suggestions || []).length === 0) {
                setAdjError('No adjustment suggestions generated.');
            }
        } catch (err: any) {
            setAdjError(getApiError(err, 'Failed to get suggestions. Please try again.'));
        } finally {
            setAdjLoading(false);
        }
    };

    const handleGenerateRetrospective = async () => {
        if (!selectedCampaignId) return;
        setRetroLoading(true);
        setRetroResult(null);
        setRetroExpanded(false);
        try {
            const res = await generateCampaignRetrospective(selectedCampaignId);
            setRetroResult(res);
            setRetroExpanded(true);
        } catch (err: any) {
            alert(getApiError(err, 'Failed to generate retrospective. Please try again.'));
        } finally {
            setRetroLoading(false);
        }
    };

    // ── Autonomous Leads Handlers ────────────────────────────────────────────

    const loadNurtureHealth = useCallback(async () => {
        setNurtureHealthLoading(true);
        try {
            const data = await getNurtureHealth();
            setNurtureHealth(data);
        } catch (err) {
            console.error('Failed to load nurture health', err);
        } finally {
            setNurtureHealthLoading(false);
        }
    }, []);

    const loadSequences = useCallback(async () => {
        try {
            const resp = await apiClient.get('/lead-capture/sequences', {
                params: projectId ? { project_id: projectId } : {},
            });
            setSequences(resp.data || []);
            if ((resp.data || []).length > 0) {
                setSelectedSequenceId(resp.data[0].sequence_id);
            }
        } catch (err) {
            console.error('Failed to load sequences', err);
        }
    }, [projectId]);

    useEffect(() => {
        if (activeTab === 'autonomous_leads') {
            loadNurtureHealth();
            loadSequences();
        }
    }, [activeTab, loadNurtureHealth, loadSequences]);

    const handleScoreLeads = async () => {
        setScoringLoading(true);
        setScoringError('');
        setScoredLeads([]);
        setSelectedLeadIds(new Set());
        setEnrollResult(null);
        try {
            const res = await scoreLeads(projectId || undefined);
            setScoredLeads(res.leads || []);
            if ((res.leads || []).length === 0) {
                setScoringError('No leads found to score. Capture some leads via lead forms first.');
            }
        } catch (err: any) {
            setScoringError(getApiError(err, 'Lead scoring failed. Please try again.'));
        } finally {
            setScoringLoading(false);
        }
    };

    const toggleLeadSelection = (leadId: string) => {
        setSelectedLeadIds(prev => {
            const next = new Set(Array.from(prev));
            if (next.has(leadId)) next.delete(leadId);
            else next.add(leadId);
            return next;
        });
    };

    const handleAutoEnroll = async () => {
        if (!selectedSequenceId || selectedLeadIds.size === 0) return;
        setEnrollLoading(true);
        setEnrollResult(null);
        try {
            const res = await autoEnrollLeads(
                Array.from(selectedLeadIds),
                selectedSequenceId,
                projectId || undefined,
            );
            setEnrollResult(res);
            setSelectedLeadIds(new Set());
            loadNurtureHealth();
        } catch (err: any) {
            alert(getApiError(err, 'Enrolment failed. Please try again.'));
        } finally {
            setEnrollLoading(false);
        }
    };

    const handleReEngage = async () => {
        const days = parseInt(reEngageDays, 10);
        if (isNaN(days) || days < 1) return;
        setReEngageLoading(true);
        setReEngageResult(null);
        try {
            const res = await reEngageLeads(days, projectId || undefined);
            setReEngageResult(res);
        } catch (err: any) {
            alert(getApiError(err, 'Re-engagement draft failed. Please try again.'));
        } finally {
            setReEngageLoading(false);
        }
    };

    // ── Autonomous Monitoring Handlers ───────────────────────────────────────

    const loadPlatformHealth = useCallback(async () => {
        setHealthLoading(true);
        try {
            const data = await getPlatformHealth();
            setPlatformHealth(data);
        } catch (err) {
            console.error('Failed to load platform health', err);
        } finally {
            setHealthLoading(false);
        }
    }, []);

    const loadRecentReports = useCallback(async () => {
        try {
            const data = await getRecentReports();
            setRecentReports(data);
        } catch (err) {
            console.error('Failed to load recent reports', err);
        }
    }, []);

    const loadReportSchedule = useCallback(async () => {
        setScheduleLoading(true);
        try {
            const data = await getReportSchedule();
            setScheduleFrequency(data.frequency || 'weekly');
            setScheduleDow(String(data.day_of_week ?? 1));
            setScheduleHour(String(data.hour ?? 9));
            setScheduleEnabled(data.enabled ?? false);
        } catch (err) {
            console.error('Failed to load report schedule', err);
        } finally {
            setScheduleLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'autonomous_monitoring') {
            loadPlatformHealth();
            loadRecentReports();
            loadReportSchedule();
        }
    }, [activeTab, loadPlatformHealth, loadRecentReports, loadReportSchedule]);

    const handleDetectAnomalies = async () => {
        setAnomalyLoading(true);
        setAnomalies([]);
        setAnomalyScanned(false);
        try {
            const res = await detectAnomalies();
            setAnomalies(res.anomalies || []);
            setAnomalyScanned(true);
        } catch (err: any) {
            alert(getApiError(err, 'Anomaly scan failed. Please try again.'));
        } finally {
            setAnomalyLoading(false);
        }
    };

    const handleGenerateExecutiveReport = async () => {
        setExecReportLoading(true);
        setExecReport(null);
        try {
            const res = await generateExecutiveReport(execReportPeriod);
            setExecReport(res);
            loadRecentReports();
        } catch (err: any) {
            alert(getApiError(err, 'Report generation failed. Please try again.'));
        } finally {
            setExecReportLoading(false);
        }
    };

    const handleLoadRecentReport = (report: any) => {
        const reportText = report.content?.report_text || report.preview || '';
        setExecReport({
            report_id: report.report_id,
            headline: report.title,
            period: report.period,
            report_text: reportText,
            generated_at: report.created_at,
        });
    };

    const handleCopyReport = () => {
        if (!execReport?.report_text) return;
        navigator.clipboard.writeText(execReport.report_text).catch(() => {});
    };

    const handleSaveSchedule = async () => {
        setScheduleSaving(true);
        try {
            await scheduleReport({
                frequency: scheduleFrequency,
                day_of_week: parseInt(scheduleDow, 10),
                hour: parseInt(scheduleHour, 10),
                enabled: scheduleEnabled,
            });
        } catch (err: any) {
            alert(getApiError(err, 'Failed to save schedule. Please try again.'));
        } finally {
            setScheduleSaving(false);
        }
    };

    // ── Competitor Handlers ─────────────────────────────────────────────────

    const handleAddCompetitor = async () => {
        if (!projectId || !newCompetitorName.trim()) return;
        setAddingCompetitor(true);
        try {
            await addCompetitor({
                project_id: projectId,
                name: newCompetitorName.trim(),
                website_url: newCompetitorUrl.trim() || undefined,
                notes: newCompetitorNotes.trim() || undefined,
            });
            setNewCompetitorName('');
            setNewCompetitorUrl('');
            setNewCompetitorNotes('');
            setAddCompetitorOpen(false);
            loadCompetitors();
        } catch (err) {
            console.error('Failed to add competitor', err);
        } finally {
            setAddingCompetitor(false);
        }
    };

    const handleDeleteCompetitor = async (id: string) => {
        if (!window.confirm('Delete this competitor and all its reports?')) return;
        try {
            await deleteCompetitor(id);
            loadCompetitors();
        } catch (err) {
            console.error('Failed to delete competitor', err);
        }
    };

    const handleAnalyzeCompetitor = async (id: string) => {
        setAnalyzingId(id);
        try {
            const report = await analyzeCompetitor(id);
            setActiveReport(report);
            setReportModalOpen(true);
            loadCompetitors();
        } catch (err) {
            console.error('Failed to analyze competitor', err);
            alert('Analysis failed. Please try again.');
        } finally {
            setAnalyzingId(null);
        }
    };

    const handleViewLatestReport = async (id: string) => {
        setReportLoading(true);
        setReportModalOpen(true);
        try {
            const report = await getLatestCompetitorReport(id);
            setActiveReport(report);
        } catch (err: any) {
            if (err?.response?.status === 404) {
                setActiveReport(null);
            }
            console.error('Failed to load report', err);
        } finally {
            setReportLoading(false);
        }
    };

    const handleMineReviews = async (id: string) => {
        setMiningId(id);
        setReviewModalOpen(true);
        setActiveInsight(null);
        try {
            const insight = await mineCompetitorReviews(id, false);
            setActiveInsight(insight);
            loadCompetitors();
        } catch (err) {
            console.error('Failed to mine reviews', err);
            alert('Review mining failed. Please try again.');
            setReviewModalOpen(false);
        } finally {
            setMiningId(null);
        }
    };

    const handleViewLatestInsight = async (id: string) => {
        setReviewLoading(true);
        setReviewModalOpen(true);
        setActiveInsight(null);
        try {
            const insight = await getLatestReviewInsight(id);
            setActiveInsight(insight);
        } catch (err: any) {
            if (err?.response?.status === 404) {
                setActiveInsight(null);
            }
            console.error('Failed to load review insight', err);
        } finally {
            setReviewLoading(false);
        }
    };

    const handlePromoteTopicsToBriefs = async (insightId: string) => {
        if (!activeInsight) return;
        setPromotingTopics(true);
        try {
            const result = await promoteReviewTopicsToBriefs(activeInsight.competitor_id, insightId);
            alert(`Added ${result.added} topic(s) to content plan. Total seeds: ${result.total_seeds}.`);
        } catch (err) {
            console.error('Failed to promote topics', err);
            alert('Failed to add topics to content plan. Please try again.');
        } finally {
            setPromotingTopics(false);
        }
    };

    // ── Calendar Handlers ───────────────────────────────────────────────────

    const handleGenerateCalendar = async () => {
        if (!projectId) return;
        setCalendarGenerating(true);
        setCurrentPlan(null);
        setExecutedItems(new Set());
        try {
            const goals = calendarGoals.split(',').map(s => s.trim()).filter(Boolean);
            const topics = calendarTopics.split(',').map(s => s.trim()).filter(Boolean);
            const plan = await generateContentCalendar({
                project_id: projectId,
                month: calendarMonth,
                goals: goals.length ? goals : undefined,
                focus_topics: topics.length ? topics : undefined,
            });
            setCurrentPlan(plan);
            loadPastPlans();
        } catch (err) {
            console.error('Failed to generate calendar', err);
            alert('Calendar generation failed. Please try again.');
        } finally {
            setCalendarGenerating(false);
        }
    };

    const handleApprovePlan = async () => {
        if (!currentPlan) return;
        try {
            const updated = await updateContentCalendar(currentPlan.plan_id, { status: 'approved' });
            setCurrentPlan(updated);
            loadPastPlans();
        } catch (err) {
            console.error('Failed to approve plan', err);
        }
    };

    const handleExecuteItem = async (index: number) => {
        if (!currentPlan) return;
        setExecutingItem(index);
        try {
            await apiClient.post(
                `/agent/content-calendar/${currentPlan.plan_id}/execute-item`,
                { item_index: index }
            );
            setExecutedItems(prev => { const next = new Set(Array.from(prev)); next.add(index); return next; });
        } catch (err) {
            console.error('Failed to create content task', err);
            alert('Failed to create content task.');
        } finally {
            setExecutingItem(null);
        }
    };

    // ── Report Handlers ─────────────────────────────────────────────────────

    const handleGenerateReport = async () => {
        if (!projectId) return;
        setReportGenerating(true);
        setCurrentReport(null);
        try {
            const report = await generateMonthlyReport({
                project_id: projectId,
                month: reportMonth,
            });
            setCurrentReport(report);
            loadPastReports();
        } catch (err) {
            console.error('Failed to generate report', err);
            alert('Report generation failed. Please try again.');
        } finally {
            setReportGenerating(false);
        }
    };

    const handleLoadPastReport = async (reportId: string) => {
        try {
            const report = await getMonthlyReport(reportId);
            setCurrentReport(report);
        } catch (err) {
            console.error('Failed to load report', err);
        }
    };

    const handleOpenHtmlReport = () => {
        if (!currentReport?.html_content) return;
        const blob = new Blob([currentReport.html_content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    // ── No Project Selected Guard ────────────────────────────────────────────

    if (!projectId) {
        return (
            <div className="agent-module">
                <ModuleHeader
                    title="AI Agent"
                    breadcrumb={['AI Agent']}
                />
                <GlassCard padding="lg">
                    <div className="agent-empty-state">
                        <div className="agent-empty-state-icon">
                            <Bot size={48} />
                        </div>
                        <h3>Select a Project</h3>
                        <p>
                            Choose a project from the sidebar to use AI Agent features including
                            competitor intelligence, content calendar planning, monthly reports, and content suggestions.
                        </p>
                    </div>
                </GlassCard>
            </div>
        );
    }

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="agent-module">
            <ModuleHeader
                title="AI Agent"
                breadcrumb={['AI Agent']}
                actions={[
                    <GlassBadge key="beta" variant="info">Beta</GlassBadge>,
                ]}
            />

            <div className="agent-tabs-row">
                <GlassTabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                />
            </div>

            {/* ── Tab 1: Competitor Intelligence ── */}
            {activeTab === 'competitors' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                            {competitors.length} competitor{competitors.length !== 1 ? 's' : ''} tracked
                        </span>
                        <GlassButton
                            variant="primary"
                            size="sm"
                            onClick={() => setAddCompetitorOpen(true)}
                            icon={<Plus size={16} />}
                        >
                            Add Competitor
                        </GlassButton>
                    </div>

                    {competitorsLoading ? (
                        <div className="agent-loading">
                            <Loader size={20} className="spin" />
                            Loading competitors…
                        </div>
                    ) : competitors.length === 0 ? (
                        <GlassCard padding="lg">
                            <div className="agent-empty-state">
                                <div className="agent-empty-state-icon">
                                    <Building2 size={48} />
                                </div>
                                <h3>No Competitors Yet</h3>
                                <p>
                                    Add competitors to get started — AiMe will analyse their marketing approach
                                    and identify opportunities for your business to differentiate.
                                </p>
                                <GlassButton variant="primary" onClick={() => setAddCompetitorOpen(true)} icon={<Plus size={16} />}>
                                    Add Competitor
                                </GlassButton>
                            </div>
                        </GlassCard>
                    ) : (
                        <div className="competitor-grid">
                            {competitors.map(comp => (
                                <GlassCard key={comp.competitor_id} padding="md" className="competitor-card">
                                    <div className="competitor-card-header">
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h3 className="competitor-card-name">{comp.name}</h3>
                                            {comp.website_url && (
                                                <a
                                                    href={comp.website_url.startsWith('http') ? comp.website_url : `https://${comp.website_url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="competitor-card-url"
                                                >
                                                    <Globe size={12} />
                                                    {comp.website_url}
                                                </a>
                                            )}
                                        </div>
                                        <button
                                            className="competitor-delete-btn"
                                            onClick={() => handleDeleteCompetitor(comp.competitor_id)}
                                            title="Delete competitor"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    {comp.notes && (
                                        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
                                            {comp.notes}
                                        </p>
                                    )}

                                    <p className="competitor-card-meta">
                                        {comp.last_analyzed_at
                                            ? `Last analysed: ${formatDate(comp.last_analyzed_at)}`
                                            : 'Not yet analysed'}
                                    </p>

                                    <div className="competitor-card-actions">
                                        <GlassButton
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleAnalyzeCompetitor(comp.competitor_id)}
                                            disabled={analyzingId === comp.competitor_id}
                                            icon={analyzingId === comp.competitor_id ? <Loader size={14} /> : <Zap size={14} />}
                                        >
                                            {analyzingId === comp.competitor_id ? 'Analysing…' : 'Analyse'}
                                        </GlassButton>
                                        {comp.last_analyzed_at && (
                                            <GlassButton
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleViewLatestReport(comp.competitor_id)}
                                                icon={<FileText size={14} />}
                                            >
                                                View Report
                                            </GlassButton>
                                        )}
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleMineReviews(comp.competitor_id)}
                                            disabled={miningId === comp.competitor_id}
                                            icon={miningId === comp.competitor_id ? <Loader size={14} /> : <MessageSquare size={14} />}
                                        >
                                            {miningId === comp.competitor_id ? 'Mining…' : 'Mine Reviews'}
                                        </GlassButton>
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleViewLatestInsight(comp.competitor_id)}
                                            icon={<Star size={14} />}
                                        >
                                            View Review Insights
                                        </GlassButton>
                                    </div>
                                </GlassCard>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Tab 2: Content Calendar ── */}
            {activeTab === 'calendar' && (
                <div>
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <div className="calendar-controls">
                            <div>
                                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Month
                                </label>
                                <input
                                    type="month"
                                    className="calendar-month-input"
                                    value={calendarMonth}
                                    onChange={e => setCalendarMonth(e.target.value)}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <GlassInput
                                    label="Goals (comma-separated)"
                                    placeholder="e.g. increase leads, brand awareness"
                                    value={calendarGoals}
                                    onChange={setCalendarGoals}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <GlassInput
                                    label="Focus Topics (comma-separated)"
                                    placeholder="e.g. estate planning, tax advice"
                                    value={calendarTopics}
                                    onChange={setCalendarTopics}
                                />
                            </div>
                            <GlassButton
                                variant="primary"
                                onClick={handleGenerateCalendar}
                                disabled={calendarGenerating || !projectId}
                                icon={calendarGenerating ? <Loader size={16} /> : <Calendar size={16} />}
                            >
                                {calendarGenerating ? 'Planning…' : 'Generate Calendar'}
                            </GlassButton>
                        </div>
                    </GlassCard>

                    {calendarGenerating && (
                        <div className="agent-loading">
                            <Loader size={20} />
                            Planning your content calendar…
                        </div>
                    )}

                    {currentPlan && !calendarGenerating && (
                        <GlassCard padding="md">
                            <div className="calendar-plan-status-row">
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                    <h3 style={{ margin: 0, fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                        Content Plan — {currentPlan.month}
                                    </h3>
                                    <GlassBadge variant={currentPlan.status === 'approved' ? 'success' : 'default'}>
                                        {currentPlan.status}
                                    </GlassBadge>
                                </div>
                                {currentPlan.status === 'draft' && (
                                    <GlassButton
                                        variant="primary"
                                        size="sm"
                                        onClick={handleApprovePlan}
                                        icon={<CheckCircle size={14} />}
                                    >
                                        Approve Calendar
                                    </GlassButton>
                                )}
                            </div>

                            {currentPlan.ai_rationale && (
                                <div className="calendar-rationale">
                                    {currentPlan.ai_rationale}
                                </div>
                            )}

                            <div style={{ overflowX: 'auto' }}>
                                <table className="calendar-table">
                                    <thead>
                                        <tr>
                                            <th>Wk</th>
                                            <th>Type</th>
                                            <th>Topic</th>
                                            <th>Platform</th>
                                            <th>Rationale</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(currentPlan.plan_data || []).map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)' }}>
                                                    W{item.week}
                                                </td>
                                                <td>
                                                    <GlassBadge variant="info" size="sm">
                                                        {contentTypeBadge(item.content_type)}
                                                    </GlassBadge>
                                                </td>
                                                <td style={{ fontWeight: 'var(--font-medium)', maxWidth: '200px' }}>
                                                    {item.topic}
                                                    {item.target_keyword && (
                                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                                            kw: {item.target_keyword}
                                                        </div>
                                                    )}
                                                    <GroundingMeta item={item} />
                                                </td>
                                                <td style={{ color: 'var(--text-secondary)' }}>{item.platform}</td>
                                                <td style={{ color: 'var(--text-secondary)', maxWidth: '180px', fontSize: 'var(--text-xs)' }}>
                                                    {item.rationale}
                                                </td>
                                                <td>
                                                    {executedItems.has(idx) ? (
                                                        <GlassBadge variant="success" size="sm">
                                                            <CheckCircle size={12} style={{ marginRight: 4 }} />
                                                            Created
                                                        </GlassBadge>
                                                    ) : (
                                                        <GlassButton
                                                            variant="secondary"
                                                            size="sm"
                                                            onClick={() => handleExecuteItem(idx)}
                                                            disabled={executingItem === idx}
                                                            icon={executingItem === idx ? <Loader size={12} /> : <Plus size={12} />}
                                                        >
                                                            Create
                                                        </GlassButton>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </GlassCard>
                    )}

                    {!currentPlan && !calendarGenerating && (
                        <GlassCard padding="lg">
                            <div className="agent-empty-state">
                                <div className="agent-empty-state-icon"><Calendar size={48} /></div>
                                <h3>Plan Your Content Month</h3>
                                <p>Select a month and click Generate Calendar. AiMe will create a strategic content plan using your goals, brand voice, and competitor intelligence.</p>
                            </div>
                        </GlassCard>
                    )}

                    {pastPlans.length > 0 && (
                        <div style={{ marginTop: 'var(--space-6)' }}>
                            <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', margin: '0 0 var(--space-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Past Plans
                            </h4>
                            <div className="past-plans-list">
                                {pastPlans.map(plan => (
                                    <div
                                        key={plan.plan_id}
                                        className="past-plan-row"
                                        onClick={() => setCurrentPlan(plan)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                            <Calendar size={16} style={{ color: 'var(--text-muted)' }} />
                                            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--text)' }}>
                                                {plan.month}
                                            </span>
                                            <GlassBadge variant={plan.status === 'approved' ? 'success' : 'default'} size="sm">
                                                {plan.status}
                                            </GlassBadge>
                                        </div>
                                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                            {(plan.plan_data || []).length} items · {formatDate(plan.created_at)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Tab 3: Monthly Report ── */}
            {activeTab === 'report' && (
                <div>
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Month
                                </label>
                                <input
                                    type="month"
                                    className="calendar-month-input"
                                    value={reportMonth}
                                    onChange={e => setReportMonth(e.target.value)}
                                />
                            </div>
                            <GlassButton
                                variant="primary"
                                onClick={handleGenerateReport}
                                disabled={reportGenerating || !projectId}
                                icon={reportGenerating ? <Loader size={16} /> : <BarChart2 size={16} />}
                            >
                                {reportGenerating ? 'Generating…' : 'Generate Report'}
                            </GlassButton>
                        </div>
                    </GlassCard>

                    {reportGenerating && (
                        <div className="agent-loading">
                            <Loader size={20} />
                            Generating your monthly report…
                        </div>
                    )}

                    {currentReport && !reportGenerating && (
                        <GlassCard padding="md">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                                <div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {currentReport.month}
                                    </div>
                                    <h2 className="report-headline">{currentReport.headline}</h2>
                                </div>
                                <GlassButton
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleOpenHtmlReport}
                                    icon={<ExternalLink size={14} />}
                                >
                                    Full HTML Report
                                </GlassButton>
                            </div>

                            {currentReport.content?.kpis && (
                                <div className="report-kpis">
                                    {Object.entries({
                                        content_created: 'Content Created',
                                        content_completed: 'Completed',
                                        campaigns_active: 'Campaigns',
                                        assets_generated: 'Assets',
                                    }).map(([key, label]) => (
                                        <div key={key} className="report-kpi-card">
                                            <div className="report-kpi-value">
                                                {(currentReport.content.kpis[key] ?? 0)}
                                            </div>
                                            <div className="report-kpi-label">{label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(currentReport.content?.sections || []).map((section, idx) => (
                                <div key={idx}>
                                    <h3 className="report-section-heading">{section.title}</h3>
                                    {section.content && (
                                        <p className="report-section-content">{section.content}</p>
                                    )}
                                    {section.items && section.items.length > 0 && (
                                        <ul className="report-section-items">
                                            {section.items.map((item, i) => (
                                                <li key={i}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </GlassCard>
                    )}

                    {!currentReport && !reportGenerating && (
                        <GlassCard padding="lg">
                            <div className="agent-empty-state">
                                <div className="agent-empty-state-icon"><BarChart2 size={48} /></div>
                                <h3>Generate Your Monthly Report</h3>
                                <p>AiMe will analyse your content, campaigns, and competitive data to produce a comprehensive marketing performance report.</p>
                            </div>
                        </GlassCard>
                    )}

                    {pastReports.length > 0 && (
                        <div style={{ marginTop: 'var(--space-6)' }}>
                            <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', margin: '0 0 var(--space-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Past Reports
                            </h4>
                            <div className="past-reports-list">
                                {pastReports.map(r => (
                                    <div
                                        key={r.report_id}
                                        className="past-report-row"
                                        onClick={() => handleLoadPastReport(r.report_id)}
                                    >
                                        <div>
                                            <div className="past-report-month">{r.month}</div>
                                            <div className="past-report-headline">{r.headline}</div>
                                        </div>
                                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                            {formatDate(r.generated_at)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── Tab 4: Content Suggestions ── */}
            {activeTab === 'suggestions' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
                        <GlassButton
                            variant="primary"
                            size="sm"
                            onClick={loadSuggestions}
                            disabled={suggestionsLoading}
                            icon={suggestionsLoading ? <Loader size={16} /> : <RefreshCw size={16} />}
                        >
                            {suggestionsLoading ? 'Loading…' : 'Refresh Suggestions'}
                        </GlassButton>
                    </div>

                    {suggestionsLoading ? (
                        <div className="agent-loading">
                            <Loader size={20} />
                            Identifying content opportunities…
                        </div>
                    ) : suggestions.length === 0 ? (
                        <GlassCard padding="lg">
                            <div className="agent-empty-state">
                                <div className="agent-empty-state-icon"><Lightbulb size={48} /></div>
                                <h3>No Suggestions Yet</h3>
                                <p>Click Refresh Suggestions to get AI-powered content gap analysis based on your existing content and competitor intelligence.</p>
                                <GlassButton variant="primary" onClick={loadSuggestions} icon={<RefreshCw size={16} />}>
                                    Get Suggestions
                                </GlassButton>
                            </div>
                        </GlassCard>
                    ) : (
                        <div className="suggestions-grid">
                            {suggestions.map((s, idx) => (
                                <GlassCard key={idx} padding="md" className="suggestion-card">
                                    <div className="suggestion-card-header">
                                        <GlassBadge variant={urgencyVariant(s.urgency)} size="sm">
                                            {s.urgency.toUpperCase()}
                                        </GlassBadge>
                                        <GlassBadge variant="info" size="sm">
                                            {contentTypeBadge(s.content_type)}
                                        </GlassBadge>
                                        <span className="suggestion-topic">{s.topic}</span>
                                    </div>
                                    <p className="suggestion-reason">{s.reason}</p>
                                    <GroundingMeta item={s} showKeyword />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => window.location.href = '/content'}
                                            icon={<Plus size={14} />}
                                        >
                                            Create this content
                                        </GlassButton>
                                    </div>
                                </GlassCard>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Tab 5: Autonomous Execution ── */}
            {activeTab === 'autonomous' && (
                <div>
                    {/* Autonomy Mode Banner */}
                    {autonomyStatusLoading ? (
                        <div className="agent-loading"><Loader size={20} /> Loading autonomy settings…</div>
                    ) : autonomySettings ? (() => {
                        const ccMode: string = autonomySettings.content_creation || 'off';
                        const cpMode: string = autonomySettings.content_publishing || 'off';
                        const ssMode: string = autonomySettings.social_scheduling || 'off';
                        const allOff = ccMode === 'off' && cpMode === 'off' && ssMode === 'off';
                        const anyAuto = ccMode === 'auto' || cpMode === 'auto' || ssMode === 'auto';
                        const paused: boolean = autonomySettings.agent_paused === true;

                        return (
                            <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                                    {paused ? (
                                        <AlertTriangle size={18} style={{ color: 'var(--color-warning, #f59e0b)', flexShrink: 0, marginTop: 2 }} />
                                    ) : allOff ? (
                                        <Shield size={18} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 2 }} />
                                    ) : anyAuto ? (
                                        <AlertTriangle size={18} style={{ color: 'var(--color-warning, #f59e0b)', flexShrink: 0, marginTop: 2 }} />
                                    ) : (
                                        <Info size={18} style={{ color: 'var(--color-primary, #2177FF)', flexShrink: 0, marginTop: 2 }} />
                                    )}
                                    <div style={{ flex: 1 }}>
                                        {paused ? (
                                            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                                Agent is paused — all actions will be queued for your approval regardless of mode settings.
                                            </p>
                                        ) : allOff ? (
                                            <>
                                                <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                                    Autonomous content is disabled. Enable it in the Autonomy Control Panel.
                                                </p>
                                                <GlassButton
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => window.location.href = '/autonomy'}
                                                    icon={<Shield size={14} />}
                                                >
                                                    Open Autonomy Control Panel
                                                </GlassButton>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center' }}>
                                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>Autonomy modes:</span>
                                                <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        Content creation:
                                                        <GlassBadge
                                                            variant={ccMode === 'auto' ? 'warning' : ccMode === 'review' ? 'info' : 'default'}
                                                            size="sm"
                                                        >
                                                            {ccMode}
                                                        </GlassBadge>
                                                    </span>
                                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        Scheduling:
                                                        <GlassBadge
                                                            variant={ssMode === 'auto' ? 'warning' : ssMode === 'review' ? 'info' : 'default'}
                                                            size="sm"
                                                        >
                                                            {ssMode}
                                                        </GlassBadge>
                                                    </span>
                                                </div>
                                                {anyAuto && (
                                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning, #f59e0b)', fontStyle: 'italic' }}>
                                                        Auto mode active — content will be created without approval
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </GlassCard>
                        );
                    })() : null}

                    {/* Execute Calendar Plan */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Play size={16} /> Execute Calendar Plan
                        </h3>

                        {pastPlans.length === 0 ? (
                            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
                                No content calendar plans found. Generate one in the Content Calendar tab first.
                            </p>
                        ) : (
                            <>
                                <div style={{ marginBottom: 'var(--space-3)' }}>
                                    <GlassSelect
                                        label="Select Calendar Plan"
                                        value={selectedPlanId}
                                        options={[
                                            { value: '', label: '— Select a plan —' },
                                            ...pastPlans.map(p => ({
                                                value: p.plan_id,
                                                label: `${p.month} (${p.status}) — ${(p.plan_data || []).length} items`,
                                            })),
                                        ]}
                                        onChange={setSelectedPlanId}
                                    />
                                </div>

                                {planToExecute && (
                                    <>
                                        <div style={{ marginBottom: 'var(--space-3)' }}>
                                            <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Select items to execute ({selectedPlanItems.size} of {(planToExecute.plan_data || []).length} selected)
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: '240px', overflowY: 'auto' }}>
                                                {(planToExecute.plan_data || []).map((item: CalendarItem, idx: number) => (
                                                    <label
                                                        key={idx}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 'var(--space-2)',
                                                            cursor: 'pointer',
                                                            padding: 'var(--space-2) var(--space-3)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            background: selectedPlanItems.has(idx) ? 'var(--panel-strong)' : 'transparent',
                                                            border: '1px solid var(--stroke)',
                                                            transition: 'background 0.15s',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedPlanItems.has(idx)}
                                                            onChange={() => toggleItemSelection(idx)}
                                                            style={{ accentColor: 'var(--color-primary, #2177FF)' }}
                                                        />
                                                        <GlassBadge variant="info" size="sm">W{item.week}</GlassBadge>
                                                        <GlassBadge variant="default" size="sm">{contentTypeBadge(item.content_type)}</GlassBadge>
                                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {item.topic}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <GlassButton
                                            variant="primary"
                                            onClick={handleExecuteCalendar}
                                            disabled={executing || selectedPlanItems.size === 0}
                                            icon={executing ? <Loader size={16} /> : <Play size={16} />}
                                        >
                                            {executing ? 'Executing…' : `Execute ${selectedPlanItems.size} Selected Item${selectedPlanItems.size !== 1 ? 's' : ''}`}
                                        </GlassButton>

                                        {execResult && (
                                            <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', marginBottom: 'var(--space-2)' }}>
                                                    Execution complete
                                                </div>
                                                <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
                                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                                        <strong style={{ color: 'var(--text)' }}>{execResult.executed}</strong> auto-created
                                                    </span>
                                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                                        <strong style={{ color: 'var(--text)' }}>{execResult.queued}</strong> queued for approval
                                                    </span>
                                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                                        <strong style={{ color: 'var(--text)' }}>{execResult.skipped}</strong> skipped
                                                    </span>
                                                </div>
                                                {(execResult.results || []).map((r: any, i: number) => (
                                                    <div key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                        <GlassBadge
                                                            variant={r.action === 'executed' ? 'success' : r.action === 'queued' ? 'info' : 'default'}
                                                            size="sm"
                                                        >
                                                            {r.action}
                                                        </GlassBadge>
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.topic}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </GlassCard>

                    {/* Quick Generate */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Zap size={16} /> Quick Generate
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                            <GlassSelect
                                label="Content Type"
                                value={genContentType}
                                options={CONTENT_TYPE_OPTIONS}
                                onChange={setGenContentType}
                            />
                            <GlassInput
                                label="Platform (optional)"
                                placeholder="e.g. LinkedIn, Blog"
                                value={genPlatform}
                                onChange={setGenPlatform}
                            />
                        </div>

                        <div style={{ marginBottom: 'var(--space-3)' }}>
                            <GlassInput
                                label="Topic"
                                placeholder="e.g. 5 tips for estate planning in 2026"
                                value={genTopic}
                                onChange={setGenTopic}
                                required
                            />
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                            <GlassInput
                                label="Target Keyword (optional)"
                                placeholder="e.g. estate planning Sydney"
                                value={genKeyword}
                                onChange={setGenKeyword}
                            />
                            <GlassInput
                                label="Content Brief (optional)"
                                placeholder="Key points to cover…"
                                value={genBrief}
                                onChange={setGenBrief}
                            />
                        </div>

                        <GlassButton
                            variant="primary"
                            onClick={handleQuickGenerate}
                            disabled={generating || !projectId || !genTopic.trim()}
                            icon={generating ? <Loader size={16} /> : <Zap size={16} />}
                        >
                            {generating ? 'Generating…' : 'Generate & Queue'}
                        </GlassButton>

                        {genResult && (
                            <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                    <GlassBadge
                                        variant={genResult.action === 'executed' ? 'success' : genResult.action === 'queued' ? 'info' : 'default'}
                                    >
                                        {genResult.action === 'executed' ? 'Auto-created' : genResult.action === 'queued' ? 'Queued for approval' : genResult.action}
                                    </GlassBadge>
                                    {genResult.action === 'queued' && (
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => window.location.href = '/autonomy'}
                                            icon={<ExternalLink size={12} />}
                                        >
                                            Review in Autonomy
                                        </GlassButton>
                                    )}
                                </div>
                                {genResult.content_preview && (
                                    <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontStyle: 'italic', lineHeight: 1.6 }}>
                                        {genResult.content_preview}
                                    </p>
                                )}
                            </div>
                        )}
                    </GlassCard>

                    {/* Autonomous Activity Stats */}
                    {autonomyStatus && (
                        <GlassCard padding="md">
                            <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <BarChart2 size={16} /> Autonomous Activity
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                <div style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                    <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary, #2177FF)' }}>
                                        {autonomyStatus.auto_created_this_week ?? 0}
                                    </div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>Auto-created this week</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                    <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary, #2177FF)' }}>
                                        {autonomyStatus.auto_scheduled_this_week ?? 0}
                                    </div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>Auto-scheduled this week</div>
                                </div>
                                <div style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', cursor: autonomyStatus.pending_approvals > 0 ? 'pointer' : 'default' }}
                                    onClick={() => autonomyStatus.pending_approvals > 0 && (window.location.href = '/autonomy')}
                                >
                                    <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: autonomyStatus.pending_approvals > 0 ? 'var(--color-warning, #f59e0b)' : 'var(--color-primary, #2177FF)' }}>
                                        {autonomyStatus.pending_approvals ?? 0}
                                    </div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                                        Pending approvals
                                        {autonomyStatus.pending_approvals > 0 && (
                                            <span style={{ color: 'var(--color-primary, #2177FF)', marginLeft: 4 }}>— Review</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                Change autonomy modes and blast radius limits in the{' '}
                                <a href="/autonomy" style={{ color: 'var(--color-primary, #2177FF)', textDecoration: 'none' }}>
                                    Autonomy Control Panel
                                </a>
                            </div>
                        </GlassCard>
                    )}
                </div>
            )}

            {/* ── Tab 6: Autonomous Campaigns ── */}
            {activeTab === 'autonomous_campaigns' && (
                <div>
                    {/* ── Opportunity Finder ── */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Target size={16} /> Opportunity Finder
                        </h3>
                        <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                            Analyse your existing campaigns and brand context to identify high-value campaign opportunities.
                        </p>
                        <GlassButton
                            variant="primary"
                            onClick={handleIdentifyOpportunities}
                            disabled={oppLoading}
                            icon={oppLoading ? <Loader size={16} /> : <Lightbulb size={16} />}
                        >
                            {oppLoading ? 'Analysing…' : 'Find Opportunities'}
                        </GlassButton>

                        {oppError && (
                            <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-danger, #ef4444)' }}>
                                {oppError}
                            </p>
                        )}

                        {opportunities.length > 0 && (
                            <div style={{ marginTop: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {opportunities.map((opp: any, idx: number) => (
                                    <div key={idx} style={{ padding: 'var(--space-4)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)', marginBottom: 'var(--space-2)', flexWrap: 'wrap' }}>
                                            <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)', color: 'var(--text)', flex: 1 }}>
                                                {opp.title}
                                            </div>
                                            <GlassBadge
                                                variant={
                                                    opp.suggested_goal === 'lead_gen' ? 'success'
                                                    : opp.suggested_goal === 'promotion' ? 'warning'
                                                    : opp.suggested_goal === 'retention' ? 'info'
                                                    : 'default'
                                                }
                                                size="sm"
                                            >
                                                {opp.suggested_goal || 'awareness'}
                                            </GlassBadge>
                                        </div>
                                        <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                            {opp.rationale}
                                        </p>
                                        {opp.suggested_audience && (
                                            <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                                Audience: {opp.suggested_audience}
                                            </p>
                                        )}
                                        <GlassButton
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleProposeBrief(opp)}
                                            disabled={briefLoading}
                                            icon={briefLoading ? <Loader size={14} /> : <FileText size={14} />}
                                        >
                                            {briefLoading ? 'Proposing…' : 'Propose Brief'}
                                        </GlassButton>
                                    </div>
                                ))}
                            </div>
                        )}
                    </GlassCard>

                    {/* ── Campaign Brief Preview ── */}
                    {proposedBrief && (
                        <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <FileText size={16} /> Campaign Brief
                            </h3>

                            {briefOutcome?.action === 'queued' && (
                                <div style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <Info size={14} style={{ color: 'var(--color-primary, #2177FF)' }} />
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                            Brief queued for approval in the Autonomy panel.
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                <div>
                                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-1)' }}>Campaign Name</div>
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', fontWeight: 'var(--font-medium)' }}>{proposedBrief.name}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-1)' }}>Goal</div>
                                    <GlassBadge variant="info" size="sm">{proposedBrief.campaign_goal}</GlassBadge>
                                </div>
                                <div>
                                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-1)' }}>Product/Service</div>
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{proposedBrief.product_service || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-1)' }}>Target Audience</div>
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>{proposedBrief.target_audience || '—'}</div>
                                </div>
                            </div>

                            {proposedBrief.key_message && (
                                <div style={{ marginBottom: 'var(--space-3)' }}>
                                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-1)' }}>Key Message</div>
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.6 }}>{proposedBrief.key_message}</div>
                                </div>
                            )}

                            {proposedBrief.suggested_assets?.length > 0 && (
                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>Assets to Generate</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                        {proposedBrief.suggested_assets.map((a: string, i: number) => (
                                            <GlassBadge key={i} variant="default" size="sm">{a.replace(/_/g, ' ')}</GlassBadge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {launchResult ? (
                                <div style={{ padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                    {launchResult.action === 'queued' ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                            <Info size={16} style={{ color: 'var(--color-primary, #2177FF)' }} />
                                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                                Campaign queued for approval.{' '}
                                                <a href="/autonomy" style={{ color: 'var(--color-primary, #2177FF)', textDecoration: 'none' }}>Review in Autonomy panel</a>
                                            </span>
                                        </div>
                                    ) : launchResult.action === 'executed' ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                            <CheckCircle size={16} style={{ color: 'var(--color-success, #22c55e)' }} />
                                            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                                Campaign auto-launched: <strong>{launchResult.campaign?.name}</strong>.{' '}
                                                <a href="/campaigns" style={{ color: 'var(--color-primary, #2177FF)', textDecoration: 'none' }}>View in Campaigns</a>
                                            </span>
                                        </div>
                                    ) : (
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                                            {launchResult.reason || 'Campaign launch skipped.'}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <GlassButton
                                    variant="primary"
                                    onClick={handleAutoLaunch}
                                    disabled={launchLoading}
                                    icon={launchLoading ? <Loader size={16} /> : <Rocket size={16} />}
                                >
                                    {launchLoading ? 'Launching…' : 'Auto-Launch Campaign'}
                                </GlassButton>
                            )}
                        </GlassCard>
                    )}

                    {/* ── Active Campaign Advisor ── */}
                    <GlassCard padding="md">
                        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <TrendingUp size={16} /> Active Campaign Advisor
                        </h3>

                        {campaignsLoading ? (
                            <div className="agent-loading"><Loader size={18} /> Loading campaigns…</div>
                        ) : campaigns.length === 0 ? (
                            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
                                No campaigns found. Create a campaign first in the Campaigns module.
                            </p>
                        ) : (
                            <>
                                <div style={{ marginBottom: 'var(--space-4)' }}>
                                    <GlassSelect
                                        label="Select Campaign"
                                        value={selectedCampaignId}
                                        options={[
                                            { value: '', label: '— Select a campaign —' },
                                            ...campaigns.map((c: any) => ({
                                                value: c.campaign_id,
                                                label: `${c.name} (${c.status})`,
                                            })),
                                        ]}
                                        onChange={setSelectedCampaignId}
                                    />
                                </div>

                                {selectedCampaignId && (
                                    <>
                                        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
                                            <GlassButton
                                                variant="secondary"
                                                onClick={handleGetAdjustments}
                                                disabled={adjLoading}
                                                icon={adjLoading ? <Loader size={16} /> : <TrendingUp size={16} />}
                                            >
                                                {adjLoading ? 'Analysing…' : 'Get Adjustment Suggestions'}
                                            </GlassButton>
                                            <GlassButton
                                                variant="secondary"
                                                onClick={handleGenerateRetrospective}
                                                disabled={retroLoading}
                                                icon={retroLoading ? <Loader size={16} /> : <ClipboardList size={16} />}
                                            >
                                                {retroLoading ? 'Generating…' : 'Generate Retrospective'}
                                            </GlassButton>
                                        </div>

                                        {adjError && (
                                            <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-danger, #ef4444)' }}>
                                                {adjError}
                                            </p>
                                        )}

                                        {adjSuggestions.length > 0 && (
                                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-3)' }}>
                                                    Adjustment Suggestions
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                                    {adjSuggestions.map((s: any, idx: number) => (
                                                        <div key={idx} style={{
                                                            padding: 'var(--space-3)',
                                                            background: 'var(--panel-strong)',
                                                            borderRadius: 'var(--radius-md)',
                                                            border: '1px solid var(--stroke)',
                                                            borderLeft: `3px solid ${s.priority === 'high' ? 'var(--color-danger, #ef4444)' : s.priority === 'medium' ? 'var(--color-warning, #f59e0b)' : 'var(--stroke)'}`,
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                                                <GlassBadge
                                                                    variant={s.priority === 'high' ? 'danger' : s.priority === 'medium' ? 'warning' : 'default'}
                                                                    size="sm"
                                                                >
                                                                    {s.priority}
                                                                </GlassBadge>
                                                                <GlassBadge variant="info" size="sm">{s.type}</GlassBadge>
                                                            </div>
                                                            <p style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.6 }}>
                                                                {s.description}
                                                            </p>
                                                            {s.action && (
                                                                <p style={{ margin: '0 0 var(--space-1)', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                                                                    Action: {s.action}
                                                                </p>
                                                            )}
                                                            {s.expected_impact && (
                                                                <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                                                    Expected impact: {s.expected_impact}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {retroResult && (
                                            <div>
                                                <button
                                                    onClick={() => setRetroExpanded(v => !v)}
                                                    style={{
                                                        background: 'var(--panel-strong)',
                                                        border: '1px solid var(--stroke)',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 'var(--space-2)',
                                                        padding: 'var(--space-3)',
                                                        width: '100%',
                                                        borderRadius: 'var(--radius-md)',
                                                        marginBottom: retroExpanded ? 'var(--space-3)' : 0,
                                                    } as React.CSSProperties}
                                                >
                                                    <ClipboardList size={16} style={{ color: 'var(--text-secondary)' }} />
                                                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', flex: 1, textAlign: 'left' }}>
                                                        Campaign Retrospective: {retroResult.campaign_name}
                                                    </span>
                                                    <GlassBadge
                                                        variant={retroResult.overall_rating === 'excellent' || retroResult.overall_rating === 'good' ? 'success' : retroResult.overall_rating === 'average' ? 'warning' : 'danger'}
                                                        size="sm"
                                                    >
                                                        {retroResult.overall_rating}
                                                    </GlassBadge>
                                                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{retroExpanded ? '▲' : '▼'}</span>
                                                </button>

                                                {retroExpanded && (
                                                    <div style={{ padding: 'var(--space-4)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                                        <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.7 }}>
                                                            {retroResult.summary}
                                                        </p>

                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                                                            <div>
                                                                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>What Worked</div>
                                                                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
                                                                    {(retroResult.what_worked || []).map((item: string, i: number) => (
                                                                        <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)', lineHeight: 1.5 }}>{item}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>What to Improve</div>
                                                                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
                                                                    {(retroResult.what_to_improve || []).map((item: string, i: number) => (
                                                                        <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)', lineHeight: 1.5 }}>{item}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        </div>

                                                        {retroResult.recommendations?.length > 0 && (
                                                            <div style={{ marginTop: 'var(--space-4)' }}>
                                                                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>Recommendations</div>
                                                                <ul style={{ margin: 0, paddingLeft: 'var(--space-5)' }}>
                                                                    {retroResult.recommendations.map((r: string, i: number) => (
                                                                        <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', marginBottom: 'var(--space-1)', lineHeight: 1.5 }}>{r}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )}

                                                        {retroResult.next_campaign_ideas?.length > 0 && (
                                                            <div style={{ marginTop: 'var(--space-4)' }}>
                                                                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>Next Campaign Ideas</div>
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                                                                    {retroResult.next_campaign_ideas.map((idea: string, i: number) => (
                                                                        <GlassBadge key={i} variant="info" size="sm">{idea}</GlassBadge>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {retroResult.key_learnings && (
                                                            <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--panel)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                                                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>Key Learnings</div>
                                                                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text)', lineHeight: 1.7, fontStyle: 'italic' }}>
                                                                    {retroResult.key_learnings}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </GlassCard>
                </div>
            )}

            {/* ── Intelligence Report Modal ── */}
            <GlassModal
                isOpen={reportModalOpen}
                onClose={() => { setReportModalOpen(false); setActiveReport(null); }}
                title="Competitor Intelligence Report"
                size="lg"
            >
                {reportLoading ? (
                    <div className="agent-loading"><Loader size={20} /> Loading report…</div>
                ) : !activeReport ? (
                    <div className="agent-empty-state">
                        <p>No report available yet. Click Analyse to generate one.</p>
                    </div>
                ) : (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                Generated {formatDate(activeReport.report_date)}
                            </span>
                            <ConfidenceBadge report={activeReport} />
                        </div>

                        {activeReport.confidence_note && (
                            <div className="confidence-note">
                                <AlertTriangle size={16} />
                                <span>{activeReport.confidence_note}</span>
                            </div>
                        )}

                        <div className="report-section">
                            <h4 className="report-section-title">Summary</h4>
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>
                                {activeReport.summary}
                            </p>
                        </div>

                        {activeReport.content_topics?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Content Topics They Publish</h4>
                                <div className="report-tags">
                                    {activeReport.content_topics.map((t, i) => (
                                        <span key={i} className="report-tag">{t}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeReport.keyword_gaps?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Keywords They Target</h4>
                                <div className="report-tags">
                                    {activeReport.keyword_gaps.map((k, i) => (
                                        <span key={i} className="report-tag">{k}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeReport.strengths?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Their Strengths</h4>
                                <ul style={{ paddingLeft: 'var(--space-5)', margin: 0 }}>
                                    {activeReport.strengths.map((s, i) => (
                                        <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {activeReport.opportunities?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Your Opportunities</h4>
                                <ul style={{ paddingLeft: 'var(--space-5)', margin: 0 }}>
                                    {activeReport.opportunities.map((o, i) => (
                                        <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', fontWeight: 'var(--font-medium)', marginBottom: 'var(--space-1)' }}>{o}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </GlassModal>

            {/* ── Review Insights Modal ── */}
            <GlassModal
                isOpen={reviewModalOpen}
                onClose={() => { setReviewModalOpen(false); setActiveInsight(null); }}
                title="Competitor Review Insights"
                size="lg"
            >
                {(miningId !== null || reviewLoading) ? (
                    <div className="agent-loading"><Loader size={20} /> {miningId !== null ? 'Mining reviews…' : 'Loading insight…'}</div>
                ) : !activeInsight ? (
                    <div className="agent-empty-state">
                        <p>No review insights available yet. Click "Mine Reviews" to fetch and analyse this competitor's Google reviews.</p>
                    </div>
                ) : (
                    <div>
                        {/* Provenance line */}
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                                Mined {activeInsight.reviews_analyzed} of {activeInsight.reviews_available ?? '?'} reviews
                                {activeInsight.avg_rating != null && ` · avg ★${activeInsight.avg_rating.toFixed(1)}`}
                                {activeInsight.provider && ` · via ${activeInsight.provider}`}
                                {activeInsight.created_at && ` · ${formatDate(activeInsight.created_at)}`}
                            </span>
                            <ConfidenceBadge report={activeInsight} kind="review" />
                        </div>

                        {activeInsight.confidence_note && (
                            <div className="confidence-note">
                                <AlertTriangle size={16} />
                                <span>{activeInsight.confidence_note}</span>
                            </div>
                        )}

                        {activeInsight.common_questions?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Common Questions</h4>
                                <div className="report-tags">
                                    {activeInsight.common_questions.map((q, i) => (
                                        <span key={i} className="report-tag">
                                            {q.question}
                                            {q.evidence_count > 1 && <span style={{ marginLeft: 'var(--space-1)', opacity: 0.7, fontSize: 'var(--text-xs)' }}>×{q.evidence_count}</span>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeInsight.pain_points?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Pain Points</h4>
                                <div className="report-tags">
                                    {activeInsight.pain_points.map((p, i) => (
                                        <span key={i} className="report-tag">
                                            {p.pain_point}
                                            {p.evidence_count > 1 && <span style={{ marginLeft: 'var(--space-1)', opacity: 0.7, fontSize: 'var(--text-xs)' }}>×{p.evidence_count}</span>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeInsight.objections?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Objections</h4>
                                <div className="report-tags">
                                    {activeInsight.objections.map((o, i) => (
                                        <span key={i} className="report-tag">
                                            {o.objection}
                                            {o.evidence_count > 1 && <span style={{ marginLeft: 'var(--space-1)', opacity: 0.7, fontSize: 'var(--text-xs)' }}>×{o.evidence_count}</span>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeInsight.derived_topics?.length > 0 && (
                            <div className="report-section">
                                <h4 className="report-section-title">Suggested Content Briefs</h4>
                                <ul style={{ paddingLeft: 'var(--space-5)', margin: '0 0 var(--space-4) 0' }}>
                                    {activeInsight.derived_topics.map((t, i) => (
                                        <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-1)' }}>
                                            <strong style={{ color: 'var(--text)' }}>{t.title}</strong>
                                            {t.target_keyword && <span style={{ color: 'var(--text-muted)', marginLeft: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>kw: {t.target_keyword}</span>}
                                            {t.angle && <span style={{ color: 'var(--text-secondary)', marginLeft: 'var(--space-2)' }}>— {t.angle}</span>}
                                        </li>
                                    ))}
                                </ul>
                                <GlassButton
                                    variant="primary"
                                    onClick={() => handlePromoteTopicsToBriefs(activeInsight.insight_id)}
                                    disabled={promotingTopics}
                                    icon={promotingTopics ? <Loader size={16} /> : <Plus size={16} />}
                                >
                                    {promotingTopics ? 'Adding…' : 'Add these to content plan'}
                                </GlassButton>
                            </div>
                        )}

                        {!activeInsight.derived_topics?.length && activeInsight.reviews_analyzed === 0 && (
                            <div className="agent-empty-state">
                                <p>No reviews were found for this competitor. Verify the business name matches their Google listing and try again.</p>
                            </div>
                        )}
                    </div>
                )}
            </GlassModal>

            {/* ── Tab 7: Autonomous Leads ── */}
            {activeTab === 'autonomous_leads' && (
                <div>
                    {/* Lead Health Dashboard */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <TrendingUp size={16} /> Lead Health Dashboard
                            </h3>
                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={loadNurtureHealth}
                                disabled={nurtureHealthLoading}
                                icon={nurtureHealthLoading ? <Loader size={14} /> : <RefreshCw size={14} />}
                            >
                                Refresh
                            </GlassButton>
                        </div>

                        {nurtureHealthLoading && !nurtureHealth ? (
                            <div className="agent-loading">
                                <Loader size={20} className="spin" />
                                Loading health stats…
                            </div>
                        ) : nurtureHealth ? (
                            <>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                    <div style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-primary, #2177FF)' }}>
                                            {nurtureHealth.total_leads_in_db ?? 0}
                                        </div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>Total Leads</div>
                                    </div>
                                    <div style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-success, #10b981)' }}>
                                            {nurtureHealth.leads_enrolled ?? 0}
                                        </div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>Enrolled</div>
                                    </div>
                                    <div style={{ textAlign: 'center', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: nurtureHealth.leads_uncontacted > 0 ? 'var(--color-warning, #f59e0b)' : 'var(--color-primary, #2177FF)' }}>
                                            {nurtureHealth.leads_uncontacted ?? 0}
                                        </div>
                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>Uncontacted</div>
                                    </div>
                                </div>
                                {nurtureHealth.suggested_action && (
                                    <div style={{ padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-start' }}>
                                        <Lightbulb size={16} style={{ color: 'var(--color-primary, #2177FF)', flexShrink: 0, marginTop: 2 }} />
                                        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                            {nurtureHealth.suggested_action}
                                        </span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                Click Refresh to load health stats.
                            </p>
                        )}
                    </GlassCard>

                    {/* Lead Scorer */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Zap size={16} /> Lead Scorer
                            </h3>
                            <GlassButton
                                variant="primary"
                                size="sm"
                                onClick={handleScoreLeads}
                                disabled={scoringLoading}
                                icon={scoringLoading ? <Loader size={14} /> : <Play size={14} />}
                            >
                                {scoringLoading ? 'Scoring…' : 'Score Leads'}
                            </GlassButton>
                        </div>
                        <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                            Gemini will analyse all captured leads and score them 0-100 based on engagement signals, completeness, and fit.
                        </p>

                        {scoringError && (
                            <p style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', color: 'var(--color-danger, #ef4444)' }}>
                                {scoringError}
                            </p>
                        )}

                        {scoredLeads.length > 0 && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                        {scoredLeads.length} leads scored — {selectedLeadIds.size} selected
                                    </span>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                            if (selectedLeadIds.size === scoredLeads.length) {
                                                setSelectedLeadIds(new Set());
                                            } else {
                                                setSelectedLeadIds(new Set(scoredLeads.map((l: any) => l.lead_id)));
                                            }
                                        }}
                                    >
                                        {selectedLeadIds.size === scoredLeads.length ? 'Deselect All' : 'Select All'}
                                    </GlassButton>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: 400, overflowY: 'auto' }}>
                                    {scoredLeads.map((lead: any) => (
                                        <div
                                            key={lead.lead_id}
                                            onClick={() => toggleLeadSelection(lead.lead_id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                                padding: 'var(--space-3)',
                                                background: selectedLeadIds.has(lead.lead_id) ? 'var(--panel-strong)' : 'transparent',
                                                borderRadius: 'var(--radius-md)',
                                                border: selectedLeadIds.has(lead.lead_id) ? '1px solid var(--color-primary, #2177FF)' : '1px solid transparent',
                                                cursor: 'pointer',
                                                transition: 'background 0.15s, border-color 0.15s',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedLeadIds.has(lead.lead_id)}
                                                onChange={() => toggleLeadSelection(lead.lead_id)}
                                                onClick={e => e.stopPropagation()}
                                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 'var(--font-semibold)', fontSize: 'var(--text-sm)', color: 'var(--text)', marginBottom: 2 }}>
                                                    {lead.name || 'Unknown'}
                                                    {lead.email && (
                                                        <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)', marginLeft: 8 }}>
                                                            {lead.email}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                                    {lead.score_reason}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexShrink: 0 }}>
                                                <GlassBadge
                                                    variant={lead.score >= 70 ? 'success' : lead.score >= 40 ? 'warning' : 'danger'}
                                                    size="sm"
                                                >
                                                    {lead.score}/100
                                                </GlassBadge>
                                                <GlassBadge
                                                    variant={lead.priority === 'hot' ? 'danger' : lead.priority === 'warm' ? 'warning' : 'default'}
                                                    size="sm"
                                                >
                                                    {lead.priority}
                                                </GlassBadge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </GlassCard>

                    {/* Enroll in Sequence panel — shown when leads are selected */}
                    {selectedLeadIds.size > 0 && (
                        <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Rocket size={16} /> Enrol {selectedLeadIds.size} Lead{selectedLeadIds.size !== 1 ? 's' : ''} in Sequence
                            </h3>

                            {sequences.length === 0 ? (
                                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                    No email sequences found. Create one in the Lead Capture module first.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                        <GlassSelect
                                            label="Email Sequence"
                                            value={selectedSequenceId}
                                            onChange={setSelectedSequenceId}
                                            options={sequences.map((s: any) => ({
                                                value: s.sequence_id,
                                                label: s.name,
                                            }))}
                                        />
                                    </div>
                                    <GlassButton
                                        variant="primary"
                                        onClick={handleAutoEnroll}
                                        disabled={enrollLoading || !selectedSequenceId}
                                        icon={enrollLoading ? <Loader size={16} /> : <Zap size={16} />}
                                    >
                                        {enrollLoading ? 'Enrolling…' : 'Auto-Enrol'}
                                    </GlassButton>
                                </div>
                            )}

                            {enrollResult && (
                                <div style={{ marginTop: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <GlassBadge variant={enrollResult.action === 'executed' ? 'success' : enrollResult.action === 'queued' ? 'info' : 'default'}>
                                            {enrollResult.action === 'executed'
                                                ? `Enrolled ${enrollResult.enrolled_count} lead${enrollResult.enrolled_count !== 1 ? 's' : ''}`
                                                : enrollResult.action === 'queued'
                                                ? 'Queued for approval'
                                                : enrollResult.action}
                                        </GlassBadge>
                                        {enrollResult.action === 'queued' && (
                                            <GlassButton variant="secondary" size="sm" onClick={() => window.location.href = '/autonomy'} icon={<ExternalLink size={12} />}>
                                                Review in Autonomy
                                            </GlassButton>
                                        )}
                                    </div>
                                    <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                        Sequence: {enrollResult.sequence_name}
                                    </p>
                                </div>
                            )}
                        </GlassCard>
                    )}

                    {/* Re-Engagement section */}
                    <GlassCard padding="md">
                        <h3 style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <RefreshCw size={16} /> Re-Engagement Campaign
                        </h3>
                        <p style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                            Identify leads that have gone quiet and draft a personalised re-engagement email.
                        </p>

                        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
                            <div style={{ width: 160 }}>
                                <GlassInput
                                    label="Days inactive"
                                    placeholder="30"
                                    value={reEngageDays}
                                    onChange={setReEngageDays}
                                />
                            </div>
                            <GlassButton
                                variant="primary"
                                onClick={handleReEngage}
                                disabled={reEngageLoading}
                                icon={reEngageLoading ? <Loader size={16} /> : <Play size={16} />}
                            >
                                {reEngageLoading ? 'Drafting…' : 'Find & Draft Re-engagement'}
                            </GlassButton>
                        </div>

                        {reEngageResult && (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                    <GlassBadge variant={reEngageResult.action === 'executed' ? 'success' : reEngageResult.action === 'queued' ? 'info' : 'default'}>
                                        {reEngageResult.action === 'executed' ? 'Auto-approved' : reEngageResult.action === 'queued' ? 'Queued for approval' : reEngageResult.action}
                                    </GlassBadge>
                                    {reEngageResult.action === 'queued' && (
                                        <GlassButton variant="secondary" size="sm" onClick={() => window.location.href = '/autonomy'} icon={<ExternalLink size={12} />}>
                                            Review in Autonomy
                                        </GlassButton>
                                    )}
                                    {reEngageResult.target_lead_count > 0 && (
                                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                            {reEngageResult.target_lead_count} lead{reEngageResult.target_lead_count !== 1 ? 's' : ''} targeted
                                        </span>
                                    )}
                                </div>

                                {reEngageResult.draft && (
                                    <div style={{ padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <div style={{ marginBottom: 'var(--space-2)' }}>
                                            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Subject
                                            </span>
                                            <p style={{ margin: '2px 0 0', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                                {reEngageResult.draft.subject}
                                            </p>
                                        </div>
                                        {reEngageResult.draft.preview_text && (
                                            <div style={{ marginBottom: 'var(--space-2)' }}>
                                                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    Preview Text
                                                </span>
                                                <p style={{ margin: '2px 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                                    {reEngageResult.draft.preview_text}
                                                </p>
                                            </div>
                                        )}
                                        {reEngageResult.draft.body_html && (
                                            <div>
                                                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    Email Body Preview
                                                </span>
                                                <div
                                                    style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', lineHeight: 1.6, maxHeight: 200, overflowY: 'auto', fontStyle: 'italic' }}
                                                    dangerouslySetInnerHTML={{ __html: reEngageResult.draft.body_html }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </GlassCard>
                </div>
            )}

            {/* ── Tab 8: Autonomous Monitoring ── */}
            {activeTab === 'autonomous_monitoring' && (
                <div>
                    {/* Platform Health */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Activity size={16} /> Platform Health
                            </h3>
                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={loadPlatformHealth}
                                disabled={healthLoading}
                                icon={healthLoading ? <Loader size={14} /> : <RefreshCw size={14} />}
                            >
                                {healthLoading ? 'Checking…' : 'Refresh'}
                            </GlassButton>
                        </div>

                        {healthLoading && !platformHealth && (
                            <div className="agent-loading"><Loader size={18} className="spin" /> Loading health data…</div>
                        )}

                        {platformHealth && (
                            <>
                                {/* Urgency banner */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', marginBottom: 'var(--space-4)' }}>
                                    <GlassBadge
                                        variant={platformHealth.urgency_level === 'green' ? 'success' : platformHealth.urgency_level === 'amber' ? 'warning' : 'danger'}
                                    >
                                        {platformHealth.urgency_level === 'green' ? 'Healthy' : platformHealth.urgency_level === 'amber' ? 'Attention' : 'Urgent'}
                                    </GlassBadge>
                                    <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text)', flex: 1 }}>
                                        {platformHealth.summary}
                                    </p>
                                </div>

                                {/* Stat grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}>
                                    {[
                                        { label: 'Active Campaigns', value: platformHealth.stats?.campaigns?.active ?? 0 },
                                        { label: 'Leads (7d)', value: platformHealth.stats?.leads?.last_7d ?? 0, trend: platformHealth.stats?.leads?.trend },
                                        { label: 'Active Sequences', value: platformHealth.stats?.sequences?.active ?? 0 },
                                        { label: 'Pending Approvals', value: platformHealth.stats?.approvals?.pending_approvals ?? 0 },
                                        { label: 'Agent Actions (wk)', value: platformHealth.stats?.agent_actions?.actions_this_week ?? 0 },
                                    ].map(stat => (
                                        <div key={stat.label} style={{ padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)', textAlign: 'center' }}>
                                            <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-bold)', color: 'var(--text)' }}>
                                                {stat.value}
                                                {stat.trend && (
                                                    <span style={{ fontSize: 'var(--text-sm)', marginLeft: 4, color: stat.trend === 'up' ? 'var(--color-success)' : stat.trend === 'down' ? 'var(--color-danger)' : 'var(--text-secondary)' }}>
                                                        {stat.trend === 'up' ? ' ▲' : stat.trend === 'down' ? ' ▼' : ''}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 2 }}>{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </GlassCard>

                    {/* Anomaly Detector */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Search size={16} /> Anomaly Detector
                            </h3>
                            <GlassButton
                                variant="primary"
                                size="sm"
                                onClick={handleDetectAnomalies}
                                disabled={anomalyLoading}
                                icon={anomalyLoading ? <Loader size={14} /> : <Search size={14} />}
                            >
                                {anomalyLoading ? 'Scanning…' : 'Scan for Anomalies'}
                            </GlassButton>
                        </div>

                        {!anomalyScanned && !anomalyLoading && (
                            <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                                Scan your platform for stuck campaigns, dormant lead forms, empty sequences, and stale approval queue items.
                            </p>
                        )}

                        {anomalyScanned && anomalies.length === 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                <CheckCircle size={16} style={{ color: 'var(--color-success)', flexShrink: 0 }} />
                                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>No anomalies detected. Your platform looks healthy.</span>
                            </div>
                        )}

                        {anomalies.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                {anomalies.map((anomaly, idx) => (
                                    <div key={idx} style={{ padding: 'var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: '1px solid var(--stroke)' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
                                            <GlassBadge variant={anomaly.severity === 'critical' ? 'danger' : anomaly.severity === 'warning' ? 'warning' : 'info'} size="sm">
                                                {anomaly.severity}
                                            </GlassBadge>
                                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {anomaly.type.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                            {anomaly.description}
                                        </p>
                                        {anomaly.suggested_fix && (
                                            <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                                <strong>Fix:</strong> {anomaly.suggested_fix}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </GlassCard>

                    {/* Executive Reports */}
                    <GlassCard padding="md" style={{ marginBottom: 'var(--space-4)' }}>
                        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <FileText size={16} /> Executive Reports
                        </h3>

                        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
                            <div style={{ width: 200 }}>
                                <GlassSelect
                                    label="Period"
                                    value={execReportPeriod}
                                    onChange={setExecReportPeriod}
                                    options={[
                                        { value: 'weekly', label: 'Weekly' },
                                        { value: 'monthly', label: 'Monthly' },
                                    ]}
                                />
                            </div>
                            <GlassButton
                                variant="primary"
                                onClick={handleGenerateExecutiveReport}
                                disabled={execReportLoading}
                                icon={execReportLoading ? <Loader size={16} /> : <BarChart2 size={16} />}
                            >
                                {execReportLoading ? 'Generating…' : 'Generate Report'}
                            </GlassButton>
                        </div>

                        {execReport && (
                            <div style={{ marginBottom: 'var(--space-4)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                        {execReport.headline}
                                    </span>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                        <GlassButton variant="secondary" size="sm" onClick={handleCopyReport} icon={<Copy size={12} />}>
                                            Copy
                                        </GlassButton>
                                    </div>
                                </div>
                                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 'var(--text-xs)', color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', maxHeight: 400, overflowY: 'auto', lineHeight: 1.7, margin: 0, fontFamily: 'inherit' }}>
                                    {execReport.report_text}
                                </pre>
                                {execReport.action && (
                                    <div style={{ marginTop: 'var(--space-2)' }}>
                                        <GlassBadge variant={execReport.action === 'executed' ? 'success' : execReport.action === 'queued' ? 'info' : 'default'} size="sm">
                                            {execReport.action === 'executed' ? 'Logged' : execReport.action === 'queued' ? 'Queued for approval' : execReport.action}
                                        </GlassBadge>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Recent Reports list */}
                        {recentReports.length > 0 && (
                            <div>
                                <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                                    Recent Reports
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                    {recentReports.map(r => (
                                        <div
                                            key={r.report_id}
                                            onClick={() => handleLoadRecentReport(r)}
                                            style={{ cursor: 'pointer', padding: 'var(--space-2) var(--space-3)', background: 'var(--panel-strong)', borderRadius: 'var(--radius-md)', border: `1px solid ${execReport?.report_id === r.report_id ? 'var(--primary)' : 'var(--stroke)'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        >
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text)', fontWeight: 'var(--font-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {r.title}
                                                </p>
                                                <p style={{ margin: '2px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                                                    {formatDate(r.created_at)} — {r.period}
                                                </p>
                                            </div>
                                            <GlassBadge variant="default" size="sm">{r.period}</GlassBadge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </GlassCard>

                    {/* Report Schedule */}
                    <GlassCard padding="md">
                        <h3 style={{ margin: '0 0 var(--space-4)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-semibold)', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Clock size={16} /> Report Schedule
                        </h3>

                        {scheduleLoading ? (
                            <div className="agent-loading"><Loader size={16} className="spin" /> Loading schedule…</div>
                        ) : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                                    <button
                                        onClick={() => setScheduleEnabled(e => !e)}
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: scheduleEnabled ? 'var(--primary)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                                        title={scheduleEnabled ? 'Disable scheduled reports' : 'Enable scheduled reports'}
                                    >
                                        {scheduleEnabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                                    </button>
                                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                                        {scheduleEnabled ? 'Scheduled reports enabled' : 'Scheduled reports disabled'}
                                    </span>
                                    <GlassBadge variant={scheduleEnabled ? 'success' : 'default'} size="sm">
                                        {scheduleEnabled ? 'Active' : 'Off'}
                                    </GlassBadge>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', opacity: scheduleEnabled ? 1 : 0.5 }}>
                                    <GlassSelect
                                        label="Frequency"
                                        value={scheduleFrequency}
                                        onChange={setScheduleFrequency}
                                        options={[
                                            { value: 'weekly', label: 'Weekly' },
                                            { value: 'monthly', label: 'Monthly' },
                                        ]}
                                    />
                                    <GlassSelect
                                        label="Day of Week"
                                        value={scheduleDow}
                                        onChange={setScheduleDow}
                                        options={[
                                            { value: '0', label: 'Monday' },
                                            { value: '1', label: 'Tuesday' },
                                            { value: '2', label: 'Wednesday' },
                                            { value: '3', label: 'Thursday' },
                                            { value: '4', label: 'Friday' },
                                            { value: '5', label: 'Saturday' },
                                            { value: '6', label: 'Sunday' },
                                        ]}
                                    />
                                    <GlassSelect
                                        label="Hour (UTC)"
                                        value={scheduleHour}
                                        onChange={setScheduleHour}
                                        options={Array.from({ length: 17 }, (_, i) => {
                                            const h = i + 6;
                                            return { value: String(h), label: `${String(h).padStart(2, '0')}:00` };
                                        })}
                                    />
                                </div>

                                <GlassButton
                                    variant="primary"
                                    onClick={handleSaveSchedule}
                                    disabled={scheduleSaving}
                                    icon={scheduleSaving ? <Loader size={16} /> : <CheckCircle size={16} />}
                                >
                                    {scheduleSaving ? 'Saving…' : 'Save Schedule'}
                                </GlassButton>
                            </>
                        )}
                    </GlassCard>
                </div>
            )}

            {/* ── Add Competitor Modal ── */}
            <GlassModal
                isOpen={addCompetitorOpen}
                onClose={() => setAddCompetitorOpen(false)}
                title="Add Competitor"
                size="sm"
                footer={
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                        <GlassButton variant="secondary" onClick={() => setAddCompetitorOpen(false)}>
                            Cancel
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={handleAddCompetitor}
                            disabled={addingCompetitor || !newCompetitorName.trim()}
                        >
                            {addingCompetitor ? 'Adding…' : 'Add Competitor'}
                        </GlassButton>
                    </div>
                }
            >
                <div className="agent-form-grid">
                    <GlassInput
                        label="Competitor Name"
                        placeholder="e.g. Rival Law Firm"
                        value={newCompetitorName}
                        onChange={setNewCompetitorName}
                        required
                    />
                    <GlassInput
                        label="Website URL (optional)"
                        placeholder="e.g. https://rivalfirm.com.au"
                        value={newCompetitorUrl}
                        onChange={setNewCompetitorUrl}
                    />
                    <GlassInput
                        label="Notes (optional)"
                        placeholder="e.g. Main competitor in estate planning space"
                        value={newCompetitorNotes}
                        onChange={setNewCompetitorNotes}
                    />
                </div>
            </GlassModal>
        </div>
    );
};

export default AgentModule;
